import {
  createActions,
  createAsyncGetters,
  createMessages,
  SB,
  WithMeta,
} from '@play-co/replicant';
import { UnionToIntersection } from '@play-co/replicant/lib/core/utils/TypeUtils';
import { ComputedProperties } from '@play-co/replicant/lib/core/ReplicantComputedProperties';

type SchemaRecord = Record<string, SB.Schema>;

// export const $DEPENDENCIES: unique symbol = Symbol('$dependencies');

type ReplicantModuleConfig<TSchema extends SchemaRecord = any> = {
  $DEPENDENCIES?: Record<string, ReplicantModuleConfig>;
  schema?: TSchema;
};

type ReplicantModuleDependency<
  T extends ReplicantModuleConfig
> = T['$DEPENDENCIES'][keyof T['$DEPENDENCIES']];

type ReplicantModulePart<
  TModule extends ReplicantModuleConfig,
  TPart extends keyof TModule,
  TDeps extends ReplicantModuleConfig | void = ReplicantModuleDependency<TModule>
> = TModule[TPart] &
  UnionToIntersection<
    TDeps extends ReplicantModuleConfig
      ? TPart extends keyof TDeps
        ? ReplicantModulePart<TDeps, TPart>
        : void
      : void
  >;

export type ReplicantModuleState<
  TModule extends ReplicantModuleConfig
> = WithMeta<
  SB.ExtractType<SB.ObjectSchema<ReplicantModulePart<TModule, 'schema'>>>
>;

export function defineAsyncGetters<
  TModule extends ReplicantModuleConfig & {
    properties?: ComputedProperties;
  }
>(module?: TModule) {
  return createAsyncGetters(
    {} as SB.Schema<ReplicantModuleState<TModule>>,
    {} as {
      computedProperties: ReplicantModulePart<TModule, 'properties'>;
    },
  );
}

export function defineActions<TModule extends ReplicantModuleConfig>(
  module?: TModule,
) {
  return createActions({} as SB.Schema<ReplicantModuleState<TModule>>);
}

export function defineMessages<TModule extends ReplicantModuleConfig>(
  module?: TModule,
) {
  return createMessages({} as SB.Schema<ReplicantModuleState<TModule>>);
}

// Passing a value of a known type to this function produces an error.
// This makes it useful in TS exhaustive checks:
// https://basarat.gitbooks.io/typescript/docs/types/discriminated-unions.html#exhaustive-checks
export function assertNever(value: never): never {
  // We throw an error in case the TS type of `value` is different from its run-time type.
  throw new Error(`Invalid value: '${value}'`);
}

export function clamp(value: number, min: number, max: number): number {
  if (min > max) return clamp(value, max, min);

  return Math.min(Math.max(value, min), max);
}

export function safeParseJSON<T>(json: string, defaultValue?: T): T {
  try {
    const value = JSON.parse(json);
    if (value) {
      return value;
    }
  } catch {
    // fall back to defaultValue
  }
  return defaultValue;
}

export function isGuestUser(): boolean {
  return process.env.LINE_GUEST === 'true' || process.env.WEBP_GUEST === 'true';
}

export function isWebpGuestUser(): boolean {
  return process.env.WEBP_GUEST === 'true';
}

export function isWebPlayable(): boolean {
  return process.env.WEB_PLAYABLE === 'true' || process.env.PLATFORM === 'mock';
}
